import { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { getOpenDefects } from '../../../lib/api';
import { useAuthStore } from '../../../store/authStore';

const REPAIR_SESSION_STORAGE_KEY = 'fieldcert-repair-session-v1';
const LEGACY_REPAIR_SESSION_STORAGE_KEY = 'kadence-repair-session-v1';

function loadRepairSessionFromStorage() {
  if (typeof window === 'undefined') return new Map();
  try {
    const raw = localStorage.getItem(REPAIR_SESSION_STORAGE_KEY) || localStorage.getItem(LEGACY_REPAIR_SESSION_STORAGE_KEY);
    if (!localStorage.getItem(REPAIR_SESSION_STORAGE_KEY) && localStorage.getItem(LEGACY_REPAIR_SESSION_STORAGE_KEY) && raw) {
      try {
        localStorage.setItem(REPAIR_SESSION_STORAGE_KEY, raw);
        localStorage.removeItem(LEGACY_REPAIR_SESSION_STORAGE_KEY);
      } catch {
        // Ignore localStorage write failures (private mode/quota).
      }
    }
    if (!raw) return new Map();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Map();
    const entries = parsed.filter((item) => Array.isArray(item) && item.length === 2);
    return new Map(entries);
  } catch {
    return new Map();
  }
}

/**
 * Page-level data + behaviour hook for RepairSessionView.
 * Owns all state, queries, effects, and handlers; the component renders only.
 */
export function useRepairSession() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const prefillSiteId = location.state?.prefillSiteId
    ? String(location.state.prefillSiteId)
    : '';
  const prefillWorkOrderNumber = location.state?.workOrderNumber
    ? String(location.state.workOrderNumber)
    : '';

  // No setter: the repair session's site is fixed at mount from navigation state.
  const [siteFilter] = useState(prefillSiteId);
  const [elementTypeFilter, setElementTypeFilter] = useState('');
  const [workOrderNumber, setWorkOrderNumber] = useState(prefillWorkOrderNumber);
  const [search, setSearch] = useState('');
  const [activeDefect, setActiveDefect] = useState(null);
  const [activeRepairRecord, setActiveRepairRecord] = useState(null);
  /** test_result_id → snapshot kept visible until user finishes session (API drops row after repair) */
  const [sessionRepaired, setSessionRepaired] = useState(() => loadRepairSessionFromStorage());
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (sessionRepaired.size === 0) {
        localStorage.removeItem(REPAIR_SESSION_STORAGE_KEY);
        return;
      }
      localStorage.setItem(
        REPAIR_SESSION_STORAGE_KEY,
        JSON.stringify(Array.from(sessionRepaired.entries()))
      );
    } catch {
      // Ignore localStorage write failures (private mode/quota).
    }
  }, [sessionRepaired]);

  // When a session is scoped to one site, only surface that site's in-session
  // repairs. The full map is kept in state/storage so an unfinished session for
  // another site is preserved, just not shown outside its own site's session.
  const scopedRepaired = useMemo(() => {
    if (!siteFilter) return sessionRepaired;
    const next = new Map();
    for (const [id, entry] of sessionRepaired) {
      if (String(entry?.defect?.site_id) === String(siteFilter)) {
        next.set(id, entry);
      }
    }
    return next;
  }, [sessionRepaired, siteFilter]);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ['openDefects', siteFilter || 'all', elementTypeFilter || 'all'],
    () =>
      getOpenDefects({
        ...(siteFilter ? { siteId: siteFilter } : {}),
        ...(elementTypeFilter ? { elementType: elementTypeFilter } : {}),
        page: 1,
        limit: 100,
      }),
    { staleTime: 0 }
  );

  const defects = useMemo(() => {
    const raw = data?.defects || [];
    const byId = new Map(raw.map((d) => [d.test_result_id, d]));
    // After a repair, GET /repairs/open no longer returns that row — merge snapshots so rows stay until "Finish"
    for (const [id, entry] of scopedRepaired) {
      if (!byId.has(id)) {
        byId.set(id, entry.defect);
      }
    }
    let merged = Array.from(byId.values());
    if (!search.trim()) return merged;
    const q = search.trim().toLowerCase();
    merged = merged.filter((d) => {
      const name = (d.asset_name || d.equipment_code || d.asset_code || '').toLowerCase();
      const building = (d.building || '').toLowerCase();
      const room = (d.room || '').toLowerCase();
      const defect = (d.defects_found || '').toLowerCase();
      const itemType = String(d.item_type || '').toLowerCase();
      return (
        name.includes(q) ||
        building.includes(q) ||
        room.includes(q) ||
        defect.includes(q) ||
        itemType.includes(q)
      );
    });
    return merged;
  }, [data, search, scopedRepaired]);

  // Group by building → room
  const grouped = useMemo(() => {
    const map = new Map();
    defects.forEach((d) => {
      const building = (d.building || '').trim() || 'Unspecified Building';
      const room = (d.room || '').trim() || 'Unspecified Room';
      const key = `${building}|||${room}`;
      if (!map.has(key)) map.set(key, { building, room, items: [] });
      map.get(key).items.push(d);
    });
    return Array.from(map.values()).sort((a, b) =>
      `${a.building} ${a.room}`.localeCompare(`${b.building} ${b.room}`, undefined, { numeric: true })
    );
  }, [defects]);

  const handleRepaired = useCallback((record, defectRow) => {
    if (!defectRow?.test_result_id) return;
    setSessionRepaired((prev) => {
      const next = new Map(prev);
      next.set(defectRow.test_result_id, {
        defect: defectRow,
        record,
        repairedAt: record?.repaired_at ?? null,
      });
      return next;
    });
    setActiveDefect(null);
    setActiveRepairRecord(null);
    queryClient.invalidateQueries(['openDefects']);
    queryClient.invalidateQueries(['siteOpenDefectsCount']);
    queryClient.invalidateQueries(['siteOpenDefectsDetail']);
    queryClient.invalidateQueries(['siteOpenDefects']);
  }, [queryClient]);

  /**
   * A before-photo-only save leaves the defect open, so it must not enter sessionRepaired.
   * Refetching brings the row back with its in_progress_repair attached.
   */
  const handleBeforePhotoSaved = useCallback(() => {
    setActiveDefect(null);
    setActiveRepairRecord(null);
    queryClient.invalidateQueries(['openDefects']);
    queryClient.invalidateQueries(['siteOpenDefectsCount']);
    queryClient.invalidateQueries(['siteOpenDefectsDetail']);
    queryClient.invalidateQueries(['siteOpenDefects']);
  }, [queryClient]);

  const performFinishSession = useCallback(() => {
    setIsFinishing(true);
    setShowFinishModal(false);

    // Clear only the current site's snapshots; any other site's unfinished
    // session stays intact. The persistence effect syncs localStorage from
    // the resulting map (removing the key when it becomes empty).
    setSessionRepaired((prev) => {
      if (!siteFilter) return new Map();
      const next = new Map();
      for (const [id, entry] of prev) {
        if (String(entry?.defect?.site_id) !== String(siteFilter)) {
          next.set(id, entry);
        }
      }
      return next;
    });
    queryClient.invalidateQueries(['openDefects']);
    queryClient.invalidateQueries(['siteOpenDefectsCount']);
    queryClient.invalidateQueries(['siteOpenDefectsDetail']);
    queryClient.invalidateQueries(['siteOpenDefects']);
    queryClient.invalidateQueries(['reports-sessions-all-sites']);
    queryClient.invalidateQueries(['open-repair-buckets-for-reports']);
    navigate('/');
    setIsFinishing(false);
  }, [queryClient, navigate, siteFilter]);

  return {
    // auth
    user,
    // filter/search state
    siteFilter,
    elementTypeFilter,
    setElementTypeFilter,
    workOrderNumber,
    setWorkOrderNumber,
    search,
    setSearch,
    // query data
    isLoading,
    error,
    refetch,
    // computed
    defects,
    grouped,
    totalOpen: data?.pagination?.totalCount ?? 0,
    // session state (scoped to the current site so other sites' unfinished
    // sessions never bleed into this one)
    sessionRepaired: scopedRepaired,
    sessionRepairedCount: scopedRepaired.size,
    // modal state
    activeDefect,
    setActiveDefect,
    activeRepairRecord,
    setActiveRepairRecord,
    showFinishModal,
    setShowFinishModal,
    isFinishing,
    // handlers
    handleRepaired,
    handleBeforePhotoSaved,
    performFinishSession,
  };
}
